import { useState, useRef } from "react";
import { FileUp, Download, Loader2, Upload, FileText, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function WordToPdf() {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFile = (uploadedFile) => {
    if (!uploadedFile.name.match(/\.(docx|doc)$/i)) {
      toast.error("Please upload a Word document (.docx or .doc)");
      return;
    }
    setFile(uploadedFile);
  };

  const handleConvert = async () => {
    if (!file) {
      toast.error("Please select a file first");
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(`${API}/convert/word-to-pdf`, formData, {
        responseType: "blob",
        headers: { "Content-Type": "multipart/form-data" }
      });

      // Download the PDF
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", file.name.replace(/\.(docx|doc)$/i, ".pdf"));
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("PDF downloaded successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Conversion failed");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const clearFile = () => {
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div className="max-w-2xl mx-auto animate-slide-in" data-testid="word-to-pdf-page">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
          <FileUp className="w-8 h-8 text-primary" />
        </div>
        <h1 className="text-3xl font-heading font-bold text-foreground">
          Word to PDF
        </h1>
        <p className="text-muted-foreground mt-2">
          Convert your Word documents to PDF format instantly
        </p>
      </div>

      {/* Upload Area */}
      <div
        className={`relative border-2 border-dashed rounded-xl p-12 transition-all duration-200 ${
          dragActive 
            ? "border-primary bg-primary/5" 
            : file 
              ? "border-primary/50 bg-primary/5" 
              : "border-border hover:border-primary/50"
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        data-testid="upload-area"
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".docx,.doc"
          onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          data-testid="file-input"
        />

        {file ? (
          <div className="text-center">
            <div className="w-16 h-16 rounded-xl bg-blue-100 flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-blue-600" />
            </div>
            <p className="font-medium text-foreground mb-1">{file.name}</p>
            <p className="text-sm text-muted-foreground mb-4">
              {(file.size / 1024).toFixed(1)} KB
            </p>
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => { e.stopPropagation(); clearFile(); }}
              className="text-destructive hover:text-destructive"
              data-testid="clear-file-btn"
            >
              <X className="w-4 h-4 mr-1" /> Remove
            </Button>
          </div>
        ) : (
          <div className="text-center">
            <Upload className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-lg font-medium text-foreground mb-2">
              Drop your Word file here
            </p>
            <p className="text-sm text-muted-foreground">
              or click to browse (.docx, .doc)
            </p>
          </div>
        )}
      </div>

      {/* Convert Button */}
      <div className="mt-6 text-center">
        <Button
          onClick={handleConvert}
          disabled={!file || loading}
          size="lg"
          className="px-8 bg-primary hover:bg-primary/90"
          data-testid="convert-btn"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Converting...
            </>
          ) : (
            <>
              <Download className="w-5 h-5 mr-2" />
              Convert to PDF
            </>
          )}
        </Button>
      </div>

      {/* Features */}
      <div className="mt-12 grid grid-cols-3 gap-6 text-center">
        <div>
          <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center mx-auto mb-2">
            <FileUp className="w-5 h-5 text-primary" />
          </div>
          <p className="text-sm font-medium text-foreground">Fast Conversion</p>
          <p className="text-xs text-muted-foreground">Instant processing</p>
        </div>
        <div>
          <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center mx-auto mb-2">
            <FileText className="w-5 h-5 text-primary" />
          </div>
          <p className="text-sm font-medium text-foreground">High Quality</p>
          <p className="text-xs text-muted-foreground">Preserves formatting</p>
        </div>
        <div>
          <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center mx-auto mb-2">
            <Download className="w-5 h-5 text-primary" />
          </div>
          <p className="text-sm font-medium text-foreground">Free to Use</p>
          <p className="text-xs text-muted-foreground">No limits</p>
        </div>
      </div>
    </div>
  );
}
